import type { AiContentJob, AiContentRun, TagRunUsage } from '@/types/api';

const COST_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumSignificantDigits: 3,
});

/**
 * An AI run's cost in USD. Runs cost fractions of a cent, so the amount keeps three
 * significant digits instead of rounding to $0.00; null (OpenRouter reported none) is said
 * as unknown, never shown as $0.
 */
export function formatAiCost(costUsd: number | null): string {
  if (costUsd === null) return 'Cost not reported';
  return COST_FORMAT.format(costUsd);
}

/**
 * The usage line under an AI generation: cost, tokens in/out, time and provider, each part
 * only when the router reported it. "$0.000129 · 1,630 in / 88 out tokens · 6.1s · NextBit"
 */
export function formatAiRunUsage(run: AiContentRun): string {
  const parts: string[] = [formatAiCost(run.costUsd)];
  // A run whose response carried no usage is stored as 0 tokens and no cost (router contract):
  // those zeros were not measured, so they are left out rather than shown as "0 in / 0 out".
  const usageMissing = run.costUsd === null && run.promptTokens === 0 && run.completionTokens === 0;
  if (run.promptTokens !== null && run.completionTokens !== null && !usageMissing) {
    const n = new Intl.NumberFormat('en-US');
    parts.push(`${n.format(run.promptTokens)} in / ${n.format(run.completionTokens)} out tokens`);
  }
  if (run.latencyMs !== null) {
    parts.push(`${(run.latencyMs / 1000).toFixed(1)}s`);
  }
  if (run.provider) {
    parts.push(run.provider);
  }
  return parts.join(' · ');
}

/** What the AI content panel says while a job is in flight. */
export function aiContentJobLabel(job: AiContentJob): string {
  switch (job.state) {
    case 'queued':
      return job.source === 'auto'
        ? 'Queued for automatic generation — waiting for the AI worker'
        : 'Queued — waiting for the AI worker';
    case 'running':
      return 'Generating title and description…';
    case 'retrying':
      return `Attempt ${String(job.attemptsMade)} failed — retrying shortly`;
  }
}

/** One photo's tag run, short enough for a card: "$0.0000412 · 1.9s". */
export function formatTagRunShort(run: TagRunUsage): string {
  return `${formatAiCost(run.costUsd)} · ${(run.latencyMs / 1000).toFixed(1)}s`;
}

/**
 * The tooltip of a tag run: model, the full usage line, the run id, and whether the router
 * replayed an earlier answer (the cost shown was charged once, by the original run).
 */
export function formatTagRunDetail(run: TagRunUsage): string {
  const replayed = run.replayed ? ' · replayed (charged once, by the original run)' : '';
  return `${run.model} · ${formatAiRunUsage(run)} · run ${run.runId}${replayed}`;
}

/**
 * The photos' latest tag runs in one line: "Latest tag runs $0.000412 · 8 photos", adding
 * "(2 not reported)" when some runs reported no cost, so a partial sum never reads as the total.
 * It is the cost of the runs whose tags are shown, not lifetime spend: earlier and failed runs
 * are only in the AI Router ledger. Null when there are no runs.
 */
export function formatTagRunsTotal(runs: readonly TagRunUsage[]): string | null {
  if (runs.length === 0) return null;
  const reported = runs.filter((run) => run.costUsd !== null);
  const unreported = runs.length - reported.length;
  const photos = `${String(runs.length)} ${runs.length === 1 ? 'photo' : 'photos'}`;
  if (reported.length === 0) return `Latest tag runs: cost not reported · ${photos}`;
  const total = reported.reduce((sum, run) => sum + (run.costUsd ?? 0), 0);
  const suffix = unreported > 0 ? ` (${String(unreported)} not reported)` : '';
  return `Latest tag runs ${formatAiCost(total)} · ${photos}${suffix}`;
}

